import React, { useState, useEffect } from 'react';
import { oneOf, number, string, bool } from 'prop-types';
import styled from 'styled-components';
import { scroller } from 'react-scroll';
import ReactMarkdown from 'react-markdown';
import { useCookies } from 'react-cookie';
import { useSelector } from 'react-redux';
import { getPOSData } from '@olxbr/mojo-jojo/selectors/pos';
import { isSwitchStepPOS } from '../libs/pos';

const Container = styled.div`
  display: ${({ show }) => (show ? 'flex' : 'none')};
`;

const CoverScreen = styled.div`
  background-color: #090909;
  bottom: 0;
  left: 0;
  opacity: 0.5;
  position: fixed;
  right: 0;
  top: 0;
  z-index: 999;
`;

const TooltipWrapper = styled.div`
  width: 100%;
  max-width: 474px;
  margin: 0 auto;
`;

const Tooltip = styled.div`
  background-color: #fff;
  border-radius: 8px;
  box-shadow: 0 0 24px 0 rgba(0,0,0,0.22), 0 24px 24px 0 rgba(0,0,0,0.3);
  box-sizing: border-box;
  position: absolute;
  z-index: 1000;
  max-width: 474px;
  padding-bottom: 24px;
  margin: 0 11px;
  overflow: hidden;
  @media(min-width: 768px) {
    width: 100%;
    margin: 0;
    padding-bottom: 32px;
  }

${({ arrow, posX }) => arrow === 'top' &&
    `&:before {
      content: '';
      position: absolute;
      top: 0;
      left: calc(${posX}% - 13px);
      width: 0;
      height: 0;
      border: 13px solid transparent;
      border-bottom-color: #fff;
      border-top: 0;
      margin-top: -12px;
}`};

${({ arrow, posX }) => arrow === 'bottom' &&
    `&:after {
      content: '';
      position: absolute;
      bottom: 0;
      left: calc(${posX}% - 13px);
      width: 0;
      height: 0;
      border: 13px solid transparent;
      border-top-color: #fff;
      border-bottom: 0;
      margin-bottom: -12px;
}`}`;

const Title = styled.h1`
  color: #4a4a4a;
  font-family: Nunito Sans;
  font-weight: 600;
  font-size: 18px
  line-height: 24px;
  text-align: center;
  letter-spacing: 0px;
  margin: 24px 24px 16px;
  padding: 0px 26px;
  @media(min-width: 768px) {
    font-size: 24px;
    line-height: 32px;
    margin: 16px 0 20px
  }
  @media(max-width: 768px) {
    padding: 0px;
  }
`;

const Description = styled(ReactMarkdown)`
  margin: 0 24px 24px;
  @media(min-width: 768px) {
    margin: 0 auto 24px;
    padding: 0px 18px;
  }
  * {
    color: #4a4a4a;
    font-family: Nunito Sans;
    font-size: 14px;
    line-height: 20px;
    text-align: center;
    font-style: normal;
    @media(min-width: 768px) {
      line-height: 24px;
      font-size: 16px;
    }
  }

  strong {
    font-weight: bold;
  }
`;

const Button = styled.div`
  font-family: 'Nunito Sans', sans-seriff;
  background-color: #f28000;
  border-radius: 20px;
  color: #fff;
  cursor: pointer;
  font-size: 14px;
  font-weight: 400;
  line-height: 16px;
  margin-top: 16px;
  margin: auto;
  max-width: 106px;
  padding: 8px 16px;
  text-align: center;
  @media(min-width: 768px) {
    font-size: 16px;
    line-height: 24px;
    max-width: 133px;
  }
`;

const ImageWrapper = styled.div`
  background-image: url(${({ background }) => background});
  min-height: 139px;
  background-size: cover;
  background-position: center;
`;

const DEFAULT_TEXTS = {
  IMAGE: 'https://static.olx.com.br/osp/novo-bundle-hv.png',
  TITLE: 'Tenha o histórico do seu veículo no anúncio e garanta mais credibilidade!',
  DESCRIPTION: 'Agora você pode adicionar as informações do histórico veicular emitido pelo DENATRAN em seu anúncio e obter mais credibilidade e confiança. Venda mais em menos tempo. Incluído **nos destaques Ouro e Diamante.**',
};

const BundleHVModal = ({
  posName, arrow, posX, hasBundleHvProduct,
}) => {
  const [showBundleHV, setShowBundleHV] = useState(true);
  const [cookies, setCookie] = useCookies(['payg-bundle-hv']);
  const { parentName } = useSelector(state => getPOSData(state));

  useEffect(() => {
    const value = cookies['payg-bundle-hv'] || '';
    if (value.includes(`${parentName}-payg-bundle-hv`)) {
      setShowBundleHV(false);
    } else if (!isSwitchStepPOS(posName)) {
      scroller.scrollTo('bundlehv-showcase', {
        duration: 1500,
        delay: 100,
        smooth: true,
        offset: -100,
      });
    }
  }, []);

  const handleClick = () => {
    setShowBundleHV(false);
    const value = cookies['payg-bundle-hv'] || '';
    if (!value.includes(`${parentName}-payg-bundle-hv`)) {
      setCookie('payg-bundle-hv', `${value}.${parentName}-payg-bundle-hv`);
    }
  };

  const { IMAGE, TITLE, DESCRIPTION } = DEFAULT_TEXTS;

  return (
    <Container show={showBundleHV && hasBundleHvProduct}>
      <CoverScreen onClick={handleClick} />
      <TooltipWrapper>
        <Tooltip name="bundlehv-showcase" arrow={arrow} posX={posX}>
          <ImageWrapper background={IMAGE} />
          <Title>{TITLE}</Title>
          <Description source={DESCRIPTION} />
          <Button onClick={handleClick}>Ok. Entendi</Button>
        </Tooltip>
      </TooltipWrapper>
    </Container>
  );
};

BundleHVModal.propTypes = {
  arrow: oneOf(['top', 'bottom', 'none']),
  posX: number,
  posName: string.isRequired,
  hasBundleHvProduct: bool.isRequired,
};

BundleHVModal.defaultProps = {
  arrow: 'none',
  posX: 50,
};

export default BundleHVModal;
